/**
 * MetroKindHeader — shared "Big City + Admin/Staff + Corp↔Field ratio"
 * chrome used on every department command-center tab that surfaces
 * employee or region-scoped data.
 *
 * Feb 2026 CEO PhD directive: the same slicer that lives on Operations
 * (Routes & Regions, all 13 Ops drilldowns), Employee Management, and
 * Attendance must appear on every remaining command-center tab so the
 * team runs the whole platform with one mental model. Extracting this
 * into a single component keeps the rollout DRY — any future ratio
 * target change (say 1 admin per 30 field agents) lives here alone.
 *
 * Usage:
 *   const [bigCity, setBigCity] = useState('Dallas');
 *   const [kind, setKind]       = useState('all');
 *   <MetroKindHeader
 *     bigCity={bigCity} onBigCity={setBigCity}
 *     kind={kind}       onKind={setKind}
 *     stats={{ admin: adminCount, staff: staffCount }}
 *     testid="blackouts"
 *   />
 */

import { Scale } from 'lucide-react';

export const BIG_CITY_TABS = ['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'];

export function MetroKindHeader({
  bigCity,
  onBigCity,
  kind = null,          // 'all' | 'admin' | 'staff' — pass null to hide the toggle
  onKind = null,
  stats = null,         // { admin: number, staff: number } — pass null to hide the ratio gauge
  testid = 'metro',
  targetStaffPerAdmin = 25,
  extraRight = null,    // any component to render on the far right
}) {
  const showKind = kind !== null && typeof onKind === 'function';
  const showGauge = stats && typeof stats.admin === 'number' && typeof stats.staff === 'number';

  let ratioText = '—';
  let ratioHealthy = false;
  if (showGauge) {
    const { admin, staff } = stats;
    ratioText = staff === 0
      ? (admin === 0 ? '—' : `${admin} : 0`)
      : `1 : ${(staff / Math.max(admin, 1)).toFixed(0)}`;
    ratioHealthy = admin > 0 && (staff / admin) <= targetStaffPerAdmin * 1.2;
  }

  return (
    <div
      className="mb-3 p-3 rounded-lg bg-zinc-900 border-2 border-[#C9A962]/40 space-y-2"
      data-testid={`${testid}-metrokind-header`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold mr-2">
            Big City
          </span>
          {BIG_CITY_TABS.map((c) => {
            const active = bigCity === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onBigCity(c)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  active
                    ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                    : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                }`}
                data-testid={`${testid}-bigcity-tab-${c.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {c}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {showGauge && (
            <span
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[11px] border ${
                ratioHealthy
                  ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-300'
                  : 'bg-amber-950/40 border-amber-500/40 text-amber-300'
              }`}
              title={`Corporate ↔ Field ratio for ${bigCity} (target: 1 admin per ${targetStaffPerAdmin} field agents)`}
              data-testid={`${testid}-ratio-gauge`}
            >
              <Scale className="h-3.5 w-3.5" />
              <span className="text-[9px] uppercase tracking-widest opacity-70">Corp↔Field</span>
              <span className="font-bold">{ratioText}</span>
              <span className="text-[9px] opacity-60">· {stats.admin}A / {stats.staff}F</span>
            </span>
          )}
          {extraRight}
        </div>
      </div>
      {showKind && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold mr-2">Filter</span>
          {[
            { k: 'all',   label: 'All' },
            { k: 'admin', label: 'Admin (Corporate)' },
            { k: 'staff', label: 'Field Staff' },
          ].map(({ k, label }) => {
            const active = kind === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onKind(k)}
                className={`px-3 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest transition-all border ${
                  active
                    ? 'bg-cyan-500 text-black border-cyan-500'
                    : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-cyan-500 hover:text-cyan-400'
                }`}
                data-testid={`${testid}-kind-${k}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Shared helpers so every tab decides "is this row an admin?" the
 * exact same way — keyed off `assigned_shift === 'admin_office'` OR
 * an admin/manager role.
 */
export const isAdminRow = (r) =>
  r?.assigned_shift === 'admin_office'
  || ['admin', 'company_manager', 'manager', 'necs_admin'].includes(r?.role);

/**
 * Filter helper: returns rows whose region_code maps to the chosen
 * big city, honouring the admin/staff kind toggle. Rows without a
 * region_code are treated as corporate-wide (visible in every metro).
 */
export function filterByMetroAndKind(rows, { bigCity, kind, bigCityByRegionCode = {} }) {
  return (rows || []).filter((r) => {
    if (kind === 'admin' && !isAdminRow(r)) return false;
    if (kind === 'staff' && isAdminRow(r)) return false;
    const rc = r.region_code || r.region;
    if (!rc) return true;
    const bc = bigCityByRegionCode[rc];
    return !bc || bc === bigCity;
  });
}

export default MetroKindHeader;
